import { apiClient as api } from "./httpClient";

export interface PatientFileRow {
  id: string;
  name: string;
  description: string | null;
  fileTypeId: string | null;
  fileTypeName: string | null;
  contentType: string;
  sizeBytes: number;
  uploadedAt: string;
  uploadedByName: string | null;
  /** Pode ABRIR o arquivo (RN-538d): autor, Administrador ou quem o "Quem pode ver" alcança. */
  canRead: boolean;
  canDelete: boolean;
  visibility: FileVisibility;
  /** Pode alterar quem pode ver: dono do arquivo ou Administrador. */
  canManage: boolean;
}

/**
 * RN-538d: author = Somente eu, team = Equipe profissional do paciente (quem atende, atendeu ou
 * supervisiona), all = Todos que alcançam o paciente, custom = perfis e/ou usuários escolhidos.
 * O autor e o Administrador abrem sempre.
 */
export type FileVisibility = "author" | "team" | "all" | "custom";

export const FILE_VISIBILITIES: { value: FileVisibility; label: string; short: string; help: string }[] = [
  // `label` é a opção no envio (voz de quem envia); `short` é a coluna e a legenda (voz de quem lê)
  { value: "author", label: "Somente eu", short: "Só quem enviou", help: "Só quem enviou o arquivo e o Administrador abrem." },
  { value: "team", label: "Equipe profissional do paciente", short: "Equipe profissional", help: "Profissionais que atendem ou já atenderam o paciente e quem supervisiona o caso." },
  { value: "all", label: "Todos", short: "Todos", help: "Todos que têm acesso a este paciente, inclusive a recepção." },
  { value: "custom", label: "Personalizado", short: "Personalizado", help: "Só os perfis e usuários escolhidos." },
];

export interface FileAccess {
  visibility: FileVisibility;
  profileIds: string[];
  userIds: string[];
}

export interface FileAccessOption {
  id: string;
  name: string;
}

export interface FileViewerPerson {
  id: string;
  name: string;
  detail: string | null;
}

export interface FileViewerShare {
  id: string;
  name: string;
  /** concessão feita em "Liberar todos os arquivos do paciente": vale em qualquer regra */
  patientWide: boolean;
}

export interface FileViewers {
  visibility: FileVisibility;
  authorName: string | null;
  careTeam: FileViewerPerson[];
  profiles: FileViewerShare[];
  users: FileViewerShare[];
}

export interface FileAccessOptions {
  profiles: FileAccessOption[];
  users: FileAccessOption[];
}

export interface PatientFileTypeOption {
  id: string;
  name: string;
}

export interface PatientFilesListResponse {
  items: PatientFileRow[];
  types: PatientFileTypeOption[];
  /** Administrador: pode liberar todos os arquivos do paciente de uma vez. */
  canManagePatientAccess: boolean;
}

export interface PatientFilesFilters {
  fileTypeId?: string;
  search?: string;
  from?: string;
  to?: string;
}

export interface PatientFileTypeRow {
  id: string;
  name: string;
  order: number;
  active: boolean;
  filesCount: number;
}

/** Espelha PatientFileRules do backend — a validação real acontece lá. */
export const MAX_FILE_SIZE_BYTES = 20 * 1024 * 1024;

export const ALLOWED_EXTENSIONS = [
  ".pdf",
  ".jpg", ".jpeg", ".png", ".webp", ".gif", ".bmp", ".heic",
  ".doc", ".docx", ".odt", ".rtf", ".txt",
  ".xls", ".xlsx", ".ods", ".csv",
  ".ppt", ".pptx",
];

export const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const patientFilesService = {
  getByPatient: async (
    patientId: string,
    filters: PatientFilesFilters = {},
  ): Promise<PatientFilesListResponse> => {
    const response = await api.get(`/patient-files/by-patient/${patientId}`, { params: filters });
    return response.data;
  },

  // Multipart: o apiClient manda Content-Type application/json por padrão e ele VAZA para o
  // FormData (a API responde 415). Anulado aqui, o browser monta o boundary sozinho.
  upload: async (
    patientId: string,
    file: File,
    fileTypeId: string | null,
    description: string,
    access?: FileAccess,
  ): Promise<string> => {
    const form = new FormData();
    form.append("file", file, file.name);
    if (fileTypeId) form.append("fileTypeId", fileTypeId);
    if (description.trim()) form.append("description", description.trim());
    if (access) {
      form.append("visibility", access.visibility);
      if (access.visibility === "custom") {
        if (access.profileIds.length) form.append("shareProfileIds", access.profileIds.join(","));
        if (access.userIds.length) form.append("shareUserIds", access.userIds.join(","));
      }
    }

    const response = await api.post(`/patient-files/by-patient/${patientId}`, form, {
      headers: { "Content-Type": null },
    });
    return response.data.id as string;
  },

  // O MinIO é interno: os bytes vêm pela API sob autenticação, então o link não pode ser um
  // href direto — baixa como blob e dispara o download com o nome original.
  download: async (id: string, fileName: string): Promise<void> => {
    const response = await api.get(`/patient-files/${id}/content`, { responseType: "blob" });
    const url = URL.createObjectURL(response.data);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  },

  /** Abre em nova aba o que o navegador sabe exibir (PDF e imagem); o resto vira download. */
  openInNewTab: async (id: string): Promise<void> => {
    const response = await api.get(`/patient-files/${id}/content`, { responseType: "blob" });
    const url = URL.createObjectURL(response.data);
    window.open(url, "_blank");
    // Revogar na hora cancelaria a aba que acabou de abrir
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  },

  remove: async (id: string): Promise<void> => {
    await api.delete(`/patient-files/${id}`);
  },

  getAccess: async (id: string): Promise<FileAccess> => {
    const response = await api.get(`/patient-files/${id}/access`);
    return response.data;
  },

  setAccess: async (id: string, access: FileAccess): Promise<void> => {
    await api.put(`/patient-files/${id}/access`, access);
  },

  /** Concessões que valem para todos os arquivos do paciente (só Administrador). */
  getPatientAccess: async (patientId: string): Promise<FileAccess> => {
    const response = await api.get(`/patient-files/by-patient/${patientId}/access`);
    return response.data;
  },

  setPatientAccess: async (patientId: string, access: Omit<FileAccess, "visibility">): Promise<void> => {
    await api.put(`/patient-files/by-patient/${patientId}/access`, { visibility: "custom", ...access });
  },

  /**
   * Quem abre o arquivo, em nomes. Sem `preview` responde pela regra gravada; com ele, pela regra
   * que o formulário está propondo. Aberto a quem alcança o paciente.
   */
  getViewers: async (id: string, preview?: FileAccess): Promise<FileViewers> => {
    const params = new URLSearchParams();
    if (preview) {
      params.set("visibility", preview.visibility);
      preview.profileIds.forEach(p => params.append("profileIds", p));
      preview.userIds.forEach(u => params.append("userIds", u));
    }
    const response = await api.get(`/patient-files/${id}/viewers`, { params });
    return response.data;
  },

  getAccessOptions: async (patientId: string): Promise<FileAccessOptions> => {
    const response = await api.get(`/patient-files/by-patient/${patientId}/access-options`);
    return response.data;
  },

  getTypes: async (includeInactive = false): Promise<PatientFileTypeRow[]> => {
    const response = await api.get(`/patient-files/types`, { params: { includeInactive } });
    return response.data;
  },

  saveType: async (payload: {
    id?: string;
    name: string;
    order: number;
    active: boolean;
  }): Promise<string> => {
    const response = await api.post(`/patient-files/types`, payload);
    return response.data.id as string;
  },

  removeType: async (id: string): Promise<void> => {
    await api.delete(`/patient-files/types/${id}`);
  },
};
